import { Representative, useWizard } from '@insify/funnel-components';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCommands } from '../../hooks/useCommands';
import { useContent } from '../../hooks/useContent';
import { useConditionsQuery, useHighCareConditions, useParamsQuery } from '../../hooks/useQueries';

import { CompanyAddressForm } from './CompanyAddressForm';
import { validation } from './CompanyAddressForm/validation';

import type { Address, AddressFormType } from '@insify/funnel-components';
import type { RepresentativeFormType } from '@insify/funnel-components/src/pages/Representative/types';

type FormType = RepresentativeFormType & AddressFormType;
type FormSubmitHandler = (data: FormType) => Promise<void>;

export const RepresentativePage = (): JSX.Element | null => {
  const { showPreviousPage, showNextPage } = useWizard();
  const { t } = useTranslation(['common', 'validation', 'funnel']);
  const { representative } = useContent() || {};
  const { sendMultipleCommands } = useCommands();
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);
  const { isStale: paramsStale, data: params = {} } = useParamsQuery();
  const { isStale: highCareConditionsStale } = useHighCareConditions();
  const { data: conditions } = useConditionsQuery();
  const [addressError, setAddressError] = useState<{ type: string; message: string }>();

  const defaultAddress = { address: params.company?.address };

  useEffect(() => {
    if (
      triggerShowingNextPage &&
      !paramsStale &&
      !highCareConditionsStale &&
      params.address &&
      params.representative?.email
    ) {
      showNextPage();
      setTriggerShowingNextPage(false);
    }
  }, [triggerShowingNextPage, paramsStale, highCareConditionsStale, params]);

  const handleSubmit: FormSubmitHandler = async ({ representative, companyName, address }) => {
    const commands = [];

    if (conditions?.companyNameEditable && companyName?.length) {
      commands.push({
        type: 'enterCompanyName',
        payload: { companyName },
      } as const);
    }

    commands.push({
      type: 'enterRepresentative',
      payload: { representative },
    } as const);

    const { geoLocation: _, ...addressWithoutGeolocation } = address;
    const addressPayload: Address = {
      ...addressWithoutGeolocation,
      country: 'FR',
    };

    commands.push({
      type: 'enterAddress',
      payload: {
        address: addressPayload,
      },
    } as const);

    await sendMultipleCommands(commands);

    setTriggerShowingNextPage(true);
  };

  const CompanyAddressFormWithErrors = useCallback(
    (props: { className?: string }) => (
      <CompanyAddressForm error={addressError} onAddressReset={() => setAddressError(undefined)} {...props} />
    ),
    [addressError],
  );

  return (
    <Representative
      disableCompanyName={!conditions?.companyNameEditable}
      formData={{ ...defaultAddress, ...params }}
      isSubmitting={triggerShowingNextPage}
      eventHandlers={{
        onSubmit: handleSubmit,
        onBack: showPreviousPage,
      }}
      content={representative}
      translations={{
        back: t('common:back'),
        forward: t('common:continue'),
        validation: {
          companyNameRequired: t('validation:representative_company_name_required'),
          firstNameRequired: t('validation:representative_first_name_required'),
          firstNameInvalid: t('validation:representative_first_name_invalid'),
          lastNameRequired: t('validation:representative_last_name_required'),
          lastNameInvalid: t('validation:representative_last_name_invalid'),
          emailRequired: t('validation:email_required'),
          emailInvalid: t('validation:email_invalid'),
          phoneRequired: t('validation:phone_required'),
          phoneInvalid: t('validation:phone_invalid'),
        },
        companyName: t('funnel:company_name'),
        firstName: t('funnel:first_name'),
        lastName: t('funnel:last_name'),
        email: t('funnel:email'),
        phone: t('funnel:phone'),
      }}
      AddressForm={CompanyAddressFormWithErrors}
      addressValidation={validation(t)}
      defaultCountry='fr'
    />
  );
};
